import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ReportService } from '../../shared/services/report.service';
import { IReport, IReportTemplateRowType, ReportStatus } from '@shared/interfaces';
import { ActivatedRoute, Router } from '@angular/router';
import { lastValueFrom, Observable, Subscription } from 'rxjs';
import { ReportBuilderService } from './services/report-builder.service';
import { SnackService } from '../../shared/services/snack.service';
import { NavigationService } from '../../shared/services/navigation.service';
import { CacheService } from '../../shared/services/cache.service';
import { ReportGetter } from './directives/ReportGetter';
import { MatDialog } from '@angular/material/dialog';
import { CanComponentDeactivate } from '../../shared/directives/can-deactivate-guard.directive';
import { DialogService } from '../../shared/services/dialog.service';
import { testReport, testReport1 } from './services/test-reports';
import { FileService, GuardService } from '@shared/services';
import { ModuleService } from '../../shared/services/module.service';
import { IS_PRODUCT_SAFETY_PROJECT, IS_TRS_PROJECT } from '../../../current-project';
import { FormGroup, FormControl } from '@angular/forms';
import { Validators } from 'ngx-editor';
import { EmbeddedImageError } from './errors/embedded-image-error';

@Component({
    selector: 'app-report-view',
    templateUrl: './report.component.html',
    styleUrls: ['./report.component.scss'],
    standalone: false
})
export class ReportComponent extends ReportGetter implements OnInit, OnDestroy, CanComponentDeactivate {
    isFirstLoaded: boolean;
    disableButtons: boolean;
    item: IReport;
    subscriptions: Subscription[] = [];
    private readonly maxLength = 300;

    private readonly fileService = inject(FileService);

    constructor(
        public reportService: ReportService,
        private activatedRoute: ActivatedRoute,
        private snack: SnackService,
        private navigationService: NavigationService,
        private cache: CacheService,
        public router: Router,
        private dialogService: DialogService,
        public guardService: GuardService,
        private changeDetector: ChangeDetectorRef,
        navigation: NavigationService,
        builder: ReportBuilderService,
        dialog: MatDialog,
        moduleService: ModuleService
    ) {
        super(builder, dialog, router, navigation, guardService, moduleService);

        this.isFirstLoaded = navigationService.isFirstLoaded;
        this.subscriptions.push(
            this.activatedRoute.params.subscribe(({ reportId }) => {
                if (reportId && reportId.indexOf('test-report') >= 0) {
                    return this.goBack();
                } else if (reportId) {
                    return this.loadData(reportId);
                } else {
                    return this.goBack();
                }
            })
        );
    }

    public ngOnInit(): void {
        document.getElementById('footer').style.display = 'none';
        document.getElementById('main-container').classList.add(...['no-padding', 'full-height']);
    }

    public ngOnDestroy(): void {
        this.subscriptions.forEach(e => e.unsubscribe());
        this.builder.reset();
        this.cache.activeReport = null;
        document.getElementById('footer').style.display = 'block';
        document.getElementById('main-container').classList.remove(...['no-padding', 'full-height']);
    }

    protected async save(signedBy?: string, language?: boolean, noSnack?: boolean): Promise<void> {
        // if (!this.isReportDataValid()) {
        //   this.snack.openSnackBarError(this.translateService.instant('snacks.error.exceedsMaxTextLength', { maxLength: this.maxLength }) as string);
        //   return;
        // }

        await super.save(signedBy, language);

        if (this.isAbleToEditAsResponsible || this.isAbleToEditAsSpecialist || this.guardService.isMaster) {
            this.disableButtons = true;

            try {
                let result: IReport;

                if (this.reportService.hasEmbeddedImages(this.report)) {
                    throw new EmbeddedImageError('This report contains embedded images. These are not allowed!');
                }

                if (language) {
                    result = await this.reportService.selectLanguage({
                        id: this.report.id,
                        selectedLanguage: this.report.selectedLanguage
                    });
                } else if (signedBy === 'specialist') {
                    result = await this.reportService.signAsSpecialist(this.report);
                } else if (signedBy === 'responsible') {
                    if (this.isAbleToSignAsSpecialist) {
                        result = await this.reportService.signForResponsible(this.report);
                    } else {
                        result = await this.reportService.signAsResponsible(this.report);
                    }
                } else {
                    result = await this.reportService.updateReport(this.report);
                }

                this.item = this.report = result;
                this.setMode();
                this.changeDetector.detectChanges();

                if (!noSnack) {
                    this.snack.openSnackBarSuccess(`Report updated successfully.`);
                }

                if (IS_TRS_PROJECT) {
                    if (signedBy === 'responsible') {
                        this.snack.openSnackBarSuccess(
                            `To download the fully signed report, click on the "Export" button.`,
                            10000
                        );
                    }
                }
            } catch (e) {
                if (e instanceof EmbeddedImageError) {
                    this.snack.openSnackBarError(e.message);
                } else {
                    this.snack.openSnackBarError('An error has occurred!');
                }
            }
            this.disableButtons = false;
        }
    }

    private isReportDataValid(): boolean {
        const textFieldIds = this.report.template.sections
            .flatMap(section => section.areas)
            .flatMap(area => area.rows)
            .flatMap(rowsArr => rowsArr)
            .filter(row => row.type === IReportTemplateRowType.textarea);

        const formGroup: { [key: string]: FormControl } = {};

        textFieldIds.forEach(field => {
            const value = this.report.reportValues[field.valueId].value || '';
            formGroup[field.valueId] = new FormControl(value, [Validators.maxLength(this.maxLength)]);
        });

        const form = new FormGroup(formGroup);
        return form.valid;
    }

    private async loadData(reportId: string) {
        try {
            this.item = this.report = this.cache.activeReport
                ? this.cache.activeReport
                : await this.reportService.getItem(reportId);

            if (this.item.isUploadedManually) {
                await this.fileService.exportReport(this.item);
                await this.router.navigate(['/reports']);
            }

            this.setMode();
        } catch (e) {
            console.error(e);
            return this.goBack();
        }
    }

    canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
        if (this.isAbleToEditAsResponsible || this.isAbleToEditAsSpecialist) {
            if (JSON.stringify(this.report) !== this.previousReport) {
                return lastValueFrom(
                    this.dialogService.openDialog({
                        title: 'generic.actions.dismissChanges',
                        message: 'generic.actions.dismissChangesDesc',
                        sureQuestion: true
                    })
                ).then(e => e?.result || false);
            }
        }
        return true;
    }

    private loadTestReport(id: string) {
        const type = id.split('-').reverse()[0];
        const currentUser = this.guardService.currentUser;
        const user = {
            id: currentUser.id,
            fullName: currentUser.userData?.fullName,
            eMail: currentUser.userData?.eMail,
            fullAccess: true
        };

        switch (type) {
            case '1':
                this.item = this.report = {
                    ...testReport,
                    responsible: {
                        ...testReport.responsible,
                        specialists: [user]
                    }
                };
                break;
            case '2':
                this.item = this.report = {
                    ...testReport1,
                    responsible: {
                        ...testReport.responsible,
                        specialists: [user, ...testReport1.responsible.specialists]
                    }
                };
                break;
            case '3':
                if (IS_TRS_PROJECT) {
                    this.item = this.report = {
                        ...testReport1,
                        responsible: {
                            ...testReport.responsible,
                            specialists: [user, ...testReport1.responsible.specialists]
                        },
                        specialistSignature: [
                            {
                                id: user.id,
                                date: new Date().toISOString(),
                                fullName: user.fullName
                            }
                        ]
                    };
                }

                if (IS_PRODUCT_SAFETY_PROJECT) {
                    this.item = this.report = {
                        ...testReport1,
                        responsible: {
                            ...testReport.responsible,
                            specialists: [user, ...testReport1.responsible.specialists]
                        },
                        specialistSignature: {
                            id: user.id,
                            date: new Date().toISOString(),
                            fullName: user.fullName
                        }
                    };
                }
                break;
            case '4':
                if (IS_TRS_PROJECT) {
                    this.item = this.report = {
                        ...testReport1,
                        responsible: {
                            ...testReport.responsible,
                            responsible: [
                                user,
                                ...testReport1.responsible.responsible.map(e => ({ ...e, fullAccess: false }))
                            ]
                        },
                        specialistSignature: [
                            {
                                id: testReport1.responsible.specialists[0].id,
                                date: new Date().toISOString(),
                                fullName: testReport1.responsible.specialists[0].fullName
                            }
                        ]
                    };
                }

                if (IS_PRODUCT_SAFETY_PROJECT) {
                    this.item = this.report = {
                        ...testReport1,
                        responsible: {
                            ...testReport.responsible,
                            responsible: [
                                user,
                                ...testReport1.responsible.responsible.map(e => ({ ...e, fullAccess: false }))
                            ]
                        },
                        specialistSignature: {
                            id: testReport1.responsible.specialists[0].id,
                            date: new Date().toISOString(),
                            fullName: testReport1.responsible.specialists[0].fullName
                        }
                    };
                }
                break;
            case '5':
                if (IS_TRS_PROJECT) {
                    this.item = this.report = {
                        ...testReport1,
                        responsible: {
                            ...testReport.responsible,
                            responsible: [
                                user,
                                ...testReport1.responsible.responsible.map(e => ({
                                    ...e,
                                    fullAccess: false
                                }))
                            ]
                        },
                        specialistSignature: [
                            {
                                id: testReport1.responsible.specialists[0].id,
                                date: new Date().toISOString(),
                                fullName: testReport1.responsible.specialists[0].fullName
                            }
                        ],
                        responsibleSignature: {
                            id: user.id,
                            fullName: user.fullName,
                            date: new Date().toISOString()
                        },
                        reportStatus: ReportStatus.released
                    };
                }
                if (IS_PRODUCT_SAFETY_PROJECT) {
                    this.item = this.report = {
                        ...testReport1,
                        responsible: {
                            ...testReport.responsible,
                            responsible: [
                                user,
                                ...testReport1.responsible.responsible.map(e => ({ ...e, fullAccess: false }))
                            ]
                        },
                        specialistSignature: {
                            id: testReport1.responsible.specialists[0].id,
                            date: new Date().toISOString(),
                            fullName: testReport1.responsible.specialists[0].fullName
                        },
                        responsibleSignature: {
                            id: user.id,
                            fullName: user.fullName,
                            date: new Date().toISOString()
                        },
                        reportStatus: ReportStatus.released
                    };
                }
                break;
            case '6':
                if (IS_TRS_PROJECT) {
                    this.item = this.report = {
                        ...testReport1,
                        responsible: {
                            ...testReport.responsible,
                            specialists: [
                                user,
                                ...testReport1.responsible.specialists.map(e => ({
                                    ...e,
                                    fullAccess: false
                                }))
                            ]
                        },
                        specialistSignature: [
                            {
                                id: user.id,
                                date: new Date().toISOString(),
                                fullName: user.fullName
                            }
                        ],
                        responsibleSignature: {
                            id: null,
                            fullName: user.fullName,
                            date: new Date().toISOString()
                        },
                        reportStatus: ReportStatus.released
                    };
                }
                if (IS_PRODUCT_SAFETY_PROJECT) {
                    this.item = this.report = {
                        ...testReport1,
                        responsible: {
                            ...testReport.responsible,
                            specialists: [
                                user,
                                ...testReport1.responsible.specialists.map(e => ({ ...e, fullAccess: false }))
                            ]
                        },
                        specialistSignature: {
                            id: user.id,
                            date: new Date().toISOString(),
                            fullName: user.fullName
                        },
                        responsibleSignature: {
                            id: null,
                            fullName: user.fullName,
                            date: new Date().toISOString()
                        },
                        reportStatus: ReportStatus.released
                    };
                }
                break;
        }

        this.setMode();
    }
}
